import React, { useState } from 'react';
import { usePlan, usePlanWrite } from './data';
import { useSpineAuthority } from '../../shared/authority/useSpineAuthority';
import type { PlanGoal, PlanMilestone, PlanProposal, PlanTodo } from '../../shared/fixtures/plan-types';
import './PlanDrawer.css';

type EntityKind = 'goal' | 'milestone' | 'todo';

interface PlanDrawerProps {
  scopeId: string;
  onClose: () => void;
  onOpenEvents?: (scopeId: string) => void;
}

/**
 * The plan drawer — the governance-plane view of one scope: goals, milestones, todos, promoted intents,
 * and AI proposals that require the operator's approval. All edit affordances gate on the scope's
 * canEdit (server-derived) and, for spine writes (promote / proposal-decide), on spine-authority so a
 * disabled control means the write would be refused — never an enabled→403.
 *
 * Marked follow-ups (see RETRO-K-W2): drag-reorder, intent progress/merge, the Spans + sign-off-gate cards.
 */
export function PlanDrawer({ scopeId, onClose, onOpenEvents }: PlanDrawerProps): React.ReactElement {
  const plan = usePlan(scopeId);
  const write = usePlanWrite(scopeId);
  const authority = useSpineAuthority();
  const scope = plan.data?.data;

  if (!scope) {
    return (
      <aside className="planDrawer" aria-label="Plan">
        <div className="planHead">
          <span className="planHeadMain planTitle">Plan</span>
          <button className="planClose" onClick={onClose} aria-label="Close plan">
            ×
          </button>
        </div>
        <div className="planBody">
          <p className="emptyLine">Loading plan…</p>
        </div>
      </aside>
    );
  }

  const canEdit = scope.canEdit;
  const canPromote = authority.canAct('event:ingest');
  const canDecide = authority.canAct('approval:decide');

  return (
    <aside className="planDrawer" aria-label="Plan">
      <header className="planHead">
        <div className="planHeadMain">
          <div className="planKicker">{scope.kindLabel}</div>
          <div className="planTitle">{scope.projectName}</div>
          {scope.postureLine && <div className="planPosture">{scope.postureLine}</div>}
          {scope.ownerName && (
            <div className="planOwnerRow">
              <span className="ownerAvatar" style={{ background: scope.ownerColor ?? 'var(--accent)' }} aria-hidden>
                {scope.ownerInitial}
              </span>
              <span className="planOwnerLabel">Owner · {scope.ownerName}</span>
            </div>
          )}
        </div>
        <button className="planClose" onClick={onClose} aria-label="Close plan" title="Close">
          ×
        </button>
      </header>

      <div className="planBody">
        <EntitySection
          title="◇ Goals"
          kind="goal"
          canEdit={canEdit}
          write={write}
          addLabel="Add goal"
          items={scope.goals}
          renderItem={(g) => <GoalBody goal={g as PlanGoal} onOpenEvents={() => onOpenEvents?.(scopeId)} />}
        />

        <EntitySection
          title="⇢ Milestones"
          kind="milestone"
          canEdit={canEdit}
          write={write}
          addLabel="Add milestone"
          items={scope.milestones}
          renderItem={(m) => <MilestoneBody milestone={m as PlanMilestone} />}
          rowVariant
        />

        <TodosSection
          todos={scope.todos}
          canEdit={canEdit}
          canPromote={canPromote}
          promoteReason={authority.disabledReason('event:ingest')}
          write={write}
        />

        {scope.proposals.length > 0 && (
          <ProposalsSection
            proposals={scope.proposals}
            canDecide={canDecide}
            decideReason={authority.disabledReason('approval:decide')}
            write={write}
          />
        )}
      </div>
    </aside>
  );
}

// ——— generic entity section (goals + milestones) ———

interface EntitySectionProps {
  title: string;
  kind: EntityKind;
  canEdit: boolean;
  addLabel: string;
  items: Array<{ id: string; text: string }>;
  write: ReturnType<typeof usePlanWrite>;
  renderItem: (item: { id: string; text: string }) => React.ReactNode;
  rowVariant?: boolean;
}

function EntitySection({ title, kind, canEdit, addLabel, items, write, renderItem, rowVariant }: EntitySectionProps): React.ReactElement {
  const [adding, setAdding] = useState(false);

  return (
    <section className="planSection">
      <div className="planSectionHead">
        <span className="planSectionTitle">{title}</span>
        {canEdit && (
          <button className="planAddBtn" onClick={() => setAdding(true)} disabled={write.isPending} aria-label={addLabel}>
            +
          </button>
        )}
      </div>

      {items.map((item) => (
        <EntityRow key={item.id} kind={kind} item={item} canEdit={canEdit} write={write} rowVariant={rowVariant}>
          {renderItem(item)}
        </EntityRow>
      ))}

      {adding && (
        <AddInput
          placeholder={`${addLabel}…`}
          onCommit={async (text) => {
            if (text.trim()) await write.addEntity(kind, text.trim());
            setAdding(false);
          }}
          onCancel={() => setAdding(false)}
        />
      )}
    </section>
  );
}

function EntityRow({
  kind,
  item,
  canEdit,
  write,
  rowVariant,
  children,
}: {
  kind: EntityKind;
  item: { id: string; text: string };
  canEdit: boolean;
  write: ReturnType<typeof usePlanWrite>;
  rowVariant?: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.text);

  const commit = async () => {
    const t = text.trim();
    if (t && t !== item.text) await write.renameEntity(kind, item.id, t);
    else if (!t) await write.deleteEntity(kind, item.id);
    setEditing(false);
  };

  return (
    <div className={rowVariant ? 'entity entityRow' : 'entity'}>
      <div className="entityTop">
        {editing ? (
          <input
            className="editInput"
            value={text}
            autoFocus
            onChange={(e) => setText(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') setEditing(false);
            }}
            aria-label={`Rename ${kind}`}
          />
        ) : (
          children
        )}
        {canEdit && !editing && (
          <>
            <button className="iconEdit" onClick={() => setEditing(true)} aria-label={`Edit ${kind}`} title="Rename">
              ✎
            </button>
            <button className="iconDel" onClick={() => write.deleteEntity(kind, item.id)} aria-label={`Delete ${kind}`} title="Delete (undoable)">
              ×
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ——— per-kind bodies ———

function GoalBody({ goal, onOpenEvents }: { goal: PlanGoal; onOpenEvents: () => void }): React.ReactElement {
  return (
    <>
      <span className="entityText">{goal.text}</span>
      {goal.provenance?.startsWith('ai') && <span className="provMarker" title={`Origin: ${goal.provenance}`}>✦ AI</span>}
      {goal.statusLabel && (
        <span className="goalStatus" style={{ color: goal.statusColor ?? 'var(--t-mid)' }}>
          {goal.statusLabel}
        </span>
      )}
      {(goal.linkedEventCount || goal.ownerInitial) && (
        <div className="entityMeta">
          {goal.linkedEventCount ? (
            <button className="linkChip" onClick={onOpenEvents}>
              {goal.linkedEventCount} event{goal.linkedEventCount === 1 ? '' : 's'}
            </button>
          ) : null}
          {goal.lifecycle && <span className="lifecycleChip">{goal.lifecycle}</span>}
          {goal.ownerInitial && (
            <span className="ownerAvatar" style={{ marginLeft: 'auto', background: goal.ownerColor ?? 'var(--accent)' }} title={`Owner · ${goal.ownerName}`}>
              {goal.ownerInitial}
            </span>
          )}
        </div>
      )}
    </>
  );
}

function MilestoneBody({ milestone }: { milestone: PlanMilestone }): React.ReactElement {
  return (
    <>
      <span className="entityGlyph" aria-hidden>◈</span>
      <span className="entityText dim">{milestone.text}</span>
      {milestone.provenance?.startsWith('ai') && <span className="provMarker" title={`Origin: ${milestone.provenance}`}>✦ AI</span>}
      {milestone.isNext && <span className="nextTag">Next</span>}
    </>
  );
}

// ——— todos (add/rename/delete + governed promote) ———

function TodosSection({
  todos,
  canEdit,
  canPromote,
  promoteReason,
  write,
}: {
  todos: PlanTodo[];
  canEdit: boolean;
  canPromote: boolean;
  promoteReason?: string;
  write: ReturnType<typeof usePlanWrite>;
}): React.ReactElement {
  const [adding, setAdding] = useState(false);

  return (
    <section className="planSection">
      <div className="planSectionHead">
        <span className="planSectionTitle">☐ Todos</span>
        {canEdit && (
          <button className="planAddBtn" onClick={() => setAdding(true)} disabled={write.isPending} aria-label="Add todo">
            +
          </button>
        )}
      </div>

      {todos.length === 0 && !adding && <p className="emptyLine">No open todos.</p>}

      {todos.map((t) => (
        <div className="entity entityRow" key={t.id}>
          <div className="entityTop">
            <span className="entityGlyphMuted" aria-hidden>☐</span>
            <span className="entityText dim">{t.text}</span>
            {t.provenance?.startsWith('ai') && <span className="provMarker" title={`Origin: ${t.provenance}`}>✦ AI</span>}
            {t.canPromote && (
              <button
                className="rowLink"
                onClick={() => write.promoteTodo(t.id)}
                disabled={!canPromote || write.isPending}
                title={promoteReason ? `Disabled: ${promoteReason}` : 'Promote this todo to a tracked intent on the spine'}
              >
                Promote
              </button>
            )}
            {canEdit && (
              <button className="iconDel" onClick={() => write.deleteEntity('todo', t.id)} aria-label="Delete todo" title="Delete (undoable)">
                ×
              </button>
            )}
          </div>
        </div>
      ))}

      {adding && (
        <AddInput
          placeholder="Add todo…"
          onCommit={async (text) => {
            if (text.trim()) await write.addEntity('todo', text.trim());
            setAdding(false);
          }}
          onCancel={() => setAdding(false)}
        />
      )}
    </section>
  );
}

// ——— AI proposals (approval gate) ———

function ProposalsSection({
  proposals,
  canDecide,
  decideReason,
  write,
}: {
  proposals: PlanProposal[];
  canDecide: boolean;
  decideReason?: string;
  write: ReturnType<typeof usePlanWrite>;
}): React.ReactElement {
  return (
    <section className="planSection">
      <div className="proposalHead">✦ AI proposals · your approval required</div>
      {proposals.map((p) => (
        <div className="proposal" key={p.id}>
          <div className="proposalMeta">
            <span className="verbPill">Proposed · {p.verbLabel}</span>
            <span className={`riskPill risk-${p.risk}`}>{p.risk} risk</span>
          </div>
          <p className="proposalText">{p.text}</p>
          <div className="proposalSub">
            Proposed by {p.proposedBy} · Affects {p.affects}
          </div>
          {p.evidenceLabel && (
            <div className="evidenceChip">
              <span aria-hidden style={{ color: 'var(--red-fg)' }}>◆</span> Evidence · {p.evidenceLabel}
            </div>
          )}
          <div className="proposalActions">
            <button
              className="approveBtn"
              onClick={() => write.decideProposal(p.id, 'approved')}
              disabled={!canDecide || write.isPending}
              title={decideReason ? `Disabled: ${decideReason}` : undefined}
            >
              {p.approveVerb}
            </button>
            <button
              className="dismissBtn"
              onClick={() => write.decideProposal(p.id, 'rejected')}
              disabled={!canDecide || write.isPending}
              title={decideReason ? `Disabled: ${decideReason}` : undefined}
            >
              Dismiss
            </button>
          </div>
        </div>
      ))}
    </section>
  );
}

// ——— shared inline add input ———

function AddInput({
  placeholder,
  onCommit,
  onCancel,
}: {
  placeholder: string;
  onCommit: (text: string) => void;
  onCancel: () => void;
}): React.ReactElement {
  const [text, setText] = useState('');
  return (
    <div style={{ marginTop: 4 }}>
      <input
        className="addInput"
        placeholder={placeholder}
        value={text}
        autoFocus
        onChange={(e) => setText(e.target.value)}
        onBlur={() => onCommit(text)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onCommit(text);
          if (e.key === 'Escape') onCancel();
        }}
        aria-label={placeholder}
      />
      <div className="addHint">↵ save · esc cancel</div>
    </div>
  );
}
